// @ts-nocheck
import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CaretDown, CaretUp, Sparkle } from '@phosphor-icons/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import { getTripImportSummary } from '../../../services/tripImport/tripImport';
import TripImportSummary from '../CreateTrip/TripImport/TripImportSummary';
import './TripImportSummaryPanel.css';

/**
 * Lets the operator reopen the "what the import did" summary from the trip itself, after the Create trip modal is
 * gone. Renders nothing for trips that were not created by an import, and when the import feature is off.
 *
 * @param {Object} props
 * @param {number|string} props.tripId
 * @param {boolean} [props.showLinks] - Show each decision's link to its place on the trip.
 */
const TripImportSummaryPanel = ({ tripId, showLinks }) => {
  const { t } = useTranslation(['trip']);
  const { enableTripImportAgent } = useFlags();
  const isEnabled =
    !!enableTripImportAgent ||
    (import.meta.env.DEV &&
      import.meta.env.VITE_ENABLE_TRIP_IMPORT_AGENT === 'true');

  const [summary, setSummary] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isEnabled || !tripId) {
      setSummary(null);
      return undefined;
    }

    let isCancelled = false;
    getTripImportSummary(tripId).then((stored) => {
      if (!isCancelled) {
        setSummary(stored);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [isEnabled, tripId]);

  if (!summary) {
    return null;
  }

  const decisionCount = summary.notes?.length || 0;

  return (
    <Box id='trip-import-summary-panel' className='trip-import-summary-panel'>
      <button
        id='trip-import-summary-panel-toggle'
        type='button'
        className='trip-import-summary-panel-toggle'
        aria-expanded={isOpen}
        aria-controls='trip-import-summary-panel-body'
        onClick={() => setIsOpen((open) => !open)}
      >
        <Sparkle
          size={18}
          weight='bold'
          className='trip-import-summary-panel-icon'
        />
        <Typography
          component='span'
          className='trip-import-summary-panel-title'
        >
          {t('tripImport.reopen.title')}
        </Typography>
        <Typography component='span' className='trip-import-summary-panel-meta'>
          {t('tripImport.reopen.decisions', { count: decisionCount })}
        </Typography>
        {isOpen ? <CaretUp size={16} /> : <CaretDown size={16} />}
      </button>

      {isOpen && (
        <Box
          id='trip-import-summary-panel-body'
          className='trip-import-summary-panel-body'
        >
          <TripImportSummary
            draft={summary}
            tripId={tripId}
            mode='reopened'
            showLinks={showLinks}
          />
        </Box>
      )}
    </Box>
  );
};

TripImportSummaryPanel.propTypes = {
  tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showLinks: PropTypes.bool
};

TripImportSummaryPanel.defaultProps = {
  tripId: null,
  showLinks: true
};

export default TripImportSummaryPanel;
